import React, { useState, useEffect, useContext } from 'react';
import { Typography, Grid, Button, Box, CircularProgress, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import { ConfigLinksDetailsContext } from '../Context/cofingLinksDetailsContext';
import NewspaperIcon from '@mui/icons-material/Newspaper';

const decodeHtml = (str) => {
    try {
        const txt = document.createElement('textarea');
        txt.innerHTML = str;
        return txt.value;
    } catch (_) { return str; }
};

const getExcerpt = (htmlText, maxLength = 150) => {
    const clean = (htmlText || '').replace(/<[^>]*>/g, ' ');
    const plain = decodeHtml(clean).replace(/\s+/g, ' ').trim();
    if (!plain) return '';
    return plain.length > maxLength ? `${plain.slice(0, maxLength).trim()}...` : plain;
};

const AR_MONTHS = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
const EN_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const formatDate = (dateStr, isRTL) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const day = String(d.getDate()).padStart(2, '0');
    const month = (isRTL ? AR_MONTHS : EN_MONTHS)[d.getMonth()];
    return `${day} ${month} ${d.getFullYear()}`;
};

const pick = (ar, en, isRTL) => (isRTL ? ar || en : en || ar) || '';

const NewsCard = ({ item, isRTL, onRead, readMoreLabel }) => {
    const dateStr = formatDate(item.publishDate, isRTL);
    const title   = pick(item.articleTitleAr, item.articleTitleEn, isRTL) || item.articleTitle || '';
    const text    = pick(item.articleTextAr,  item.articleTextEn,  isRTL) || item.articleText  || '';
    return (
        <Box
            onClick={() => onRead(item.articleId)}
            sx={{
                display: 'flex',
                flexDirection: 'row',
                border: '1px solid #e8ecf0',
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                backgroundColor: '#ffffff',
                width: '100%',
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': { boxShadow: '0 4px 18px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' },
                height: '100%',
            }}
        >
            {/* Image on the right in RTL = first in DOM */}
            {item.coverImageUrl && (
                <Box
                    component='img'
                    src={item.coverImageUrl}
                    alt={title}
                    sx={{
                        width: { xs: 110, sm: 140, md: 160 },
                        flexShrink: 0,
                        objectFit: 'cover',
                    }}
                />
            )}

            {/* Text Block */}
            <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ flex: 1, minWidth: 0, p: { xs: 1.5, md: 2 }, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {dateStr && (
                    <Typography variant='caption' sx={{ color: 'text.disabled', fontWeight: 500 }}>
                        {dateStr}
                    </Typography>
                )}
                <Typography
                    variant='subtitle1'
                    fontWeight='bold'
                    sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        lineHeight: 1.5,
                        mb: 0.5,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant='body2'
                    sx={{
                        color: '#64748b',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        flex: 1,
                        lineHeight: 1.7,
                    }}
                >
                    {getExcerpt(text)}
                </Typography>
                <Typography
                    variant='body2'
                    color='primary.main'
                    sx={{ mt: 0.5, fontWeight: 500 }}
                >
                    {readMoreLabel} ...
                </Typography>
            </Box>
        </Box>
    );
};

const LatestNewsWidget = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { configLinks, isConfigLoaded } = useContext(ConfigLinksDetailsContext);
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const isRTL = i18n.language?.startsWith('ar');
    const newsCategoryId = Number(configLinks?.newsCatId);

    useEffect(() => {
        if (!isConfigLoaded) return;

        if (!(newsCategoryId > 0)) {
            setNews([]);
            setLoading(false);
            return;
        }

        let isCancelled = false;

        const fetchNews = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`/CmsArticle/${newsCategoryId}`);
                let fetched = Array.isArray(response) ? response : (response?.dataList || []);
                const excludeIds = [configLinks?.tcArticleId, configLinks?.policyArticleId].filter(Boolean);
                if (excludeIds.length > 0) fetched = fetched.filter(a => !excludeIds.includes(a.articleId));
                if (!isCancelled) {
                    setNews(fetched.slice(0, 8));
                }
            } catch (error) {
                console.error('Error fetching latest news:', error);
                if (!isCancelled) {
                    setNews([]);
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        fetchNews();

        return () => {
            isCancelled = true;
        };
    }, [configLinks?.policyArticleId, configLinks?.tcArticleId, isConfigLoaded, newsCategoryId]);

    if (!isConfigLoaded || !(newsCategoryId > 0)) return null;
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress size={30} /></Box>;
    if (news.length === 0) return null;

    const count = news.length;
    // Fill the row evenly: 1→12, 2→6, 3→4, 4+→3
    const mdCols = count >= 4 ? 3 : Math.floor(12 / count);
    const smCols = count >= 2 ? 6 : 12;

    return (
        <Box sx={{ my: { xs: 2, md: 3 } }} dir={isRTL ? 'rtl' : 'ltr'}>
            <Paper elevation={0} sx={{ borderRadius: 3, p: { xs: 2, md: 3 }, bgcolor: '#fff' }}>
                <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
                    <Typography variant='h5' fontWeight='700' color='primary.dark' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NewspaperIcon color='primary' />
                        {t('latest_news', 'آخر الأخبار')}
                    </Typography>
                    <Button variant='outlined' size='small' onClick={() => navigate('/news')} sx={{ borderRadius: 8 }}>
                        {t('view_all_news', 'عرض كل الأخبار')}
                    </Button>
                </Box>

                <Grid container spacing={2}>
                    {news.map((item) => (
                        <Grid item xs={12} sm={smCols} md={mdCols} key={item.articleId} sx={{ display: 'flex' }}>
                            <NewsCard
                                item={item}
                                isRTL={isRTL}
                                onRead={(id) => navigate('/news/' + id)}
                                readMoreLabel={t('read_more', 'اقرأ المزيد')}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Paper>
        </Box>
    );
};

export default LatestNewsWidget;
